/**
 * UI3DComponent provides a bridge between 3D world objects and 2D UI components.
 * Automatically calculates the UI component's 2D position by projecting its own 3D
 * position to screen space each frame.
 */
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { AsyncTaskWorker, IAsyncLoadableTag } from '../../utils/IAsyncLoadable.js';
import { property } from '../../utils/serialization/decorator.js';
import { SceneComponent } from '../SceneComponent.js';

import type { BaseUIComponent } from '../../ui/components/BaseUIComponent.js';
import type { IAsyncLoadable } from '../../utils/IAsyncLoadable.js';
import type { SceneComponentOptions } from '../SceneComponent.js';

export interface UI3DComponentOptions extends SceneComponentOptions {
  uiComponent?: BaseUIComponent<any>;
  screenMargin?: number;
  hideWhenBehindCamera?: boolean;
  clampToScreenBounds?: boolean;
  screenOffset?: { x: number; y: number };

  distanceScaling?: {
    baseDistance: number;
    minScale: number;
    maxScale: number;
  };
}

@EngineClass('UI3DComponent')
export class UI3DComponent extends SceneComponent implements IAsyncLoadable {
  readonly [IAsyncLoadableTag] = true;



  @property()
  public screenMargin: number = 10;

  @property()
  public hideWhenBehindCamera: boolean = true;

  @property()
  public clampToScreenBounds: boolean = true;

  // TODO LRK: Test whether Object is supported in this format.
  @property({ type: 'object', fields: { x: { type: 'number' }, y: { type: 'number' } } })
  public screenOffset: { x: number; y: number } | null = null;

  @property({ type: 'object', fields: { baseDistance: { type: 'number' }, minScale: { type: 'number' }, maxScale: { type: 'number' } } })
  public distanceScaling: {
    baseDistance: number;
    minScale: number;
    maxScale: number;
  } | null = null;

  private uiComponent: BaseUIComponent<any> | null = null;  // TODO: serialize this

  private tempWorldPosition: THREE.Vector3 = new THREE.Vector3();
  private tempCameraWorldPosition: THREE.Vector3 = new THREE.Vector3();
  private tempScreenPosition: THREE.Vector3 = new THREE.Vector3();
  private isUIComponentReady = false;
  private _asyncTaskWorker: AsyncTaskWorker = new AsyncTaskWorker();

  public override initialize(options?: UI3DComponentOptions): void {
    super.initialize(options);
    this.screenMargin = options?.screenMargin ?? this.screenMargin;
    this.hideWhenBehindCamera = options?.hideWhenBehindCamera ?? this.hideWhenBehindCamera;
    this.clampToScreenBounds = options?.clampToScreenBounds ?? this.clampToScreenBounds;
    this.screenOffset = options?.screenOffset ?? this.screenOffset;
    this.distanceScaling = options?.distanceScaling ?? this.distanceScaling;

    if (options?.uiComponent) {
      this.setUIComponent(options.uiComponent);
    }
  }

  public override beginPlay(): void {
    super.beginPlay();
    if (this.uiComponent && !this.isUIComponentReady) {
      this.initializeUIComponent();
    }
  }

  public override endPlay(): void {
    this.detachUIComponent();
    super.endPlay();
  }

  // start IAsyncLoadable implementation
  public async waitForLoad(): Promise<void> {
    await this._asyncTaskWorker.waitUntilComplete();
  }

  public isLoading(): boolean {
    return this._asyncTaskWorker.isRunning();
  }
  // end IAsyncLoadable implementation

  /**
   * Set the UI component to be managed by this 3D component
   */
  public async setUIComponent(uiComponent: BaseUIComponent<any>): Promise<void> {
    // Detach existing component if any
    if (this.uiComponent) {
      this.detachUIComponent();
    }

    this.uiComponent = uiComponent;

    // If the component is already playing, initialize the UI component immediately
    if (this.getWorld()) {
      await this.initializeUIComponent();
    }
  }

  /**
   * Get the managed UI component
   */
  public getUIComponent(): BaseUIComponent<any> | null {
    return this.uiComponent;
  }

  /**
   * Set the visibility of the UI component
   */
  public setVisible(visible: boolean): void {
    if (this.uiComponent && this.isUIComponentReady) {
      if (visible) {
        this.uiComponent.show();
      } else {
        this.uiComponent.hide();
      }
    }
  }

  private async initializeUIComponent(): Promise<void> {
    await this._asyncTaskWorker.runTask(async () => {
      if (!this.uiComponent) {
        return;
      }

      try {
      // Initialize the UI component if not already initialized
        if (!this.uiComponent.getIsInitialized()) {
          await this.uiComponent.initialize();
        }

        // Set initial visibility
        this.uiComponent.show();

        this.isUIComponentReady = true;
      } catch (error) {
        console.error('UI3DComponent: Failed to initialize UI component:', error);
      }
    });
  }

  private detachUIComponent(): void {
    if (this.uiComponent && this.isUIComponentReady) {
      this.uiComponent.hide();
      this.isUIComponentReady = false;
    }
    // Note: We don't destroy the UI component as it might be used elsewhere
    // The caller is responsible for component lifecycle management
    this.uiComponent = null;
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);

    if (!this.uiComponent || !this.isUIComponentReady) {
      return;
    }

    const camera = this.getWorld()?.getActiveCamera();
    const windowSize = this.getWorld()?.getRenderWindowSize();
    if (!camera || !windowSize) {
      return;
    }

    // Calculate world position with offset
    this.getWorldPosition(this.tempWorldPosition);

    // Project 3D position to screen coordinates
    this.tempScreenPosition.copy(this.tempWorldPosition).project(camera);

    const containerSize = windowSize;
    const screenX = (this.tempScreenPosition.x * 0.5 + 0.5) * containerSize.width;
    const screenY = (this.tempScreenPosition.y * -0.5 + 0.5) * containerSize.height;

    // Calculate distance-based scale
    let distanceScale = 1.0;
    if (this.distanceScaling) {
      camera.getWorldPosition(this.tempCameraWorldPosition);
      const distanceToCamera = this.tempWorldPosition.distanceTo(this.tempCameraWorldPosition);
      const baseDistance = this.distanceScaling.baseDistance;
      const minScale = this.distanceScaling.minScale;
      const maxScale = this.distanceScaling.maxScale;

      // Scale inversely with distance (closer = larger, farther = smaller)
      distanceScale = baseDistance / distanceToCamera;
      distanceScale = Math.max(minScale, Math.min(maxScale, distanceScale));
    }

    // Apply screen offset
    const offset = this.screenOffset ?? { x: 0, y: 0 };
    const finalX = screenX + offset.x * distanceScale;
    const finalY = screenY + offset.y * distanceScale;

    // Check visibility conditions
    const margin = this.screenMargin;
    const isVisible = this.shouldUIBeVisible(finalX, finalY, containerSize, margin);

    if (isVisible) {
      // Clamp to screen bounds if enabled
      let clampedX = finalX;
      let clampedY = finalY;

      if (this.clampToScreenBounds) {
        clampedX = Math.max(margin, Math.min(finalX, containerSize.width - margin));
        clampedY = Math.max(margin, Math.min(finalY, containerSize.height - margin));
      }

      // Update UI component position and scale using BaseUIComponent's setPosition method
      this.uiComponent.setPosition({
        left: `${clampedX}px`,
        top: `${clampedY}px`,
        position: 'absolute',
        transform: `translate(-50%, -50%) scale(${distanceScale})`,
        transformOrigin: 'center'
      });

      this.uiComponent.show();
    } else {
      this.uiComponent.hide();
    }
  }

  private shouldUIBeVisible(
    x: number,
    y: number,
    containerSize: { width: number; height: number },
    margin: number
  ): boolean {
    // Check if behind camera
    if (this.hideWhenBehindCamera && (this.tempScreenPosition.z > 1 || this.tempScreenPosition.z < 0)) {
      return false;
    }

    // Check if outside screen bounds
    if (x < margin || x > containerSize.width - margin ||
      y < margin || y > containerSize.height - margin) {
      return false;
    }

    return true;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_UI3D';
  }
}
